/**
 * MER-536: section grid layout math for the customer dashboard.
 *
 * A tab renders as a stack of sections (grouped by `sectionGroupKey`).
 * Each section is, in turn, a stack of authored rows: every widget carries
 * a `grid.row` index, and widgets sharing a row are meant to sit on one
 * line. Until MER-536 the renderer dropped every widget into a single
 * `grid-auto-flow: row dense` grid and let CSS repack them by span, which
 * (a) ignored the authored `grid.row` and (b) left single-number KPI rows
 * short of the full width -- a row of three span-3 cards filled 9 of 12
 * tracks and stranded the right quarter.
 *
 * `planSectionRows` rebuilds the intended layout: group the section's
 * widgets into their authored rows, and for a row that is *entirely*
 * single-number KPI cards, distribute the tracks so the row fills the
 * width (3 cards -> span 4, 2 -> span 6). Mixed rows (a chart or table
 * sharing the line) and lone KPI cards keep their authored, breakpoint-
 * scaled spans -- the caller renders each row in its own sub-grid so a
 * partial row can never let the next row's cards backfill into its gap.
 *
 * This module is plain `.mjs` so `node --test` exercises the layout math
 * directly; the `.tsx` caller (CustomerDashboard) is pinned by the
 * source-text assertions in scripts/section-grid.test.mjs.
 */

/**
 * MER-528: how many columns the tile grid uses at the current viewport.
 * Reducing the track count at breakpoints lets tiles reflow
 * (12 -> 8 -> 4 -> stacked) instead of cramming, and keeps the grid from
 * ever exceeding the viewport (no horizontal PAGE scroll). Bands mirror
 * the MER-528 test matrix: full 12 at >=1280 CSS px, 8 across 1024-1279,
 * 4 below 1024, single stacked column on phones.
 *
 * @param {number} width  viewport width in CSS px
 * @returns {number} active column track count
 */
export function columnsForWidth(width) {
  if (width >= 1280) return 12;
  if (width >= 1024) return 8;
  if (width >= 640) return 4;
  return 1;
}

/**
 * MER-528: scale a manifest-authored 12-grid span to the active column
 * count, preserving the tile's relative width and clamping so a tile can
 * never span more tracks than exist (which would create implicit columns
 * and overflow the page). Used for every widget that is NOT part of a
 * filled KPI row (charts, tables, lone KPI cards, mixed rows).
 *
 * @param {number} authoredSpan  the manifest `grid.span` (1..12)
 * @param {number} cols          active column track count
 * @returns {number} span in the active grid
 */
export function responsiveSpan(authoredSpan, cols) {
  if (cols >= 12) return authoredSpan;
  const scaled = Math.round((authoredSpan / 12) * cols);
  return Math.max(1, Math.min(cols, scaled));
}

/**
 * MER-536: distribute `cols` tracks across `count` equal cards so the row
 * fills the full width with no empty gutter. When the cards divide the
 * width evenly every card is equal (3 of 12 -> [4,4,4], 2 -> [6,6]); when
 * they do not, the remainder tracks are handed to the trailing cards so
 * the spans still sum to exactly `cols` (5 of 12 -> [2,2,2,3,3]). When
 * there are more cards than tracks (narrow breakpoints, e.g. cols=1 on a
 * phone) every card takes a single track and the row wraps -- the only
 * case where a row cannot fill exactly.
 *
 * @param {number} count  number of cards on the row (>=1)
 * @param {number} cols   active column track count (>=1)
 * @returns {number[]} per-card spans, length `count`
 */
export function distributeRowSpans(count, cols) {
  // Coalesce non-finite inputs to 1 so a degenerate caller gets a single
  // stacked track rather than `new Array(NaN)` throwing (the floor()/max()
  // clamp below already handles 0, negatives, and oversized values).
  const n = Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1;
  const c = Number.isFinite(cols) ? Math.max(1, Math.floor(cols)) : 1;
  const base = Math.floor(c / n);
  if (base < 1) {
    // More cards than tracks: one track each, wrap. Cannot fill exactly.
    return new Array(n).fill(1);
  }
  const remainder = c - base * n; // 0..n-1 spare tracks
  const spans = new Array(n);
  for (let i = 0; i < n; i += 1) {
    // Hand the spare tracks to the trailing cards so the row sums to `cols`.
    spans[i] = base + (i >= n - remainder ? 1 : 0);
  }
  return spans;
}

/**
 * MER-536: plan one rendered section's layout as an ordered list of rows.
 *
 * Widgets are grouped into contiguous runs that share a `grid.row`; a
 * widget with no numeric row starts its own row (defensive -- current
 * manifests always author a row). A run that is entirely single-number
 * KPI cards AND has 2+ cards is "filled" (spans distributed across `cols`
 * via distributeRowSpans). Every other run -- lone KPI cards, charts,
 * tables, or rows that mix a KPI with a non-KPI -- keeps each widget's
 * authored span projected onto `cols` via responsiveSpan, exactly as
 * before MER-536.
 *
 * @param {Array<{id: string, row?: number|null, isSingleNumber: boolean, authoredSpan: number}>} widgets
 *   the section's widgets in render (manifest) order
 * @param {number} cols  active column track count
 * @returns {Array<{row: number|null, fill: boolean, items: Array<{id: string, span: number}>}>}
 */
export function planSectionRows(widgets, cols) {
  /** @type {Array<{row: number|null, members: typeof widgets}>} */
  const runs = [];
  let prevRow;
  for (const w of widgets) {
    const row = typeof w.row === "number" ? w.row : null;
    const sameRun = runs.length > 0 && row !== null && row === prevRow;
    if (!sameRun) runs.push({ row, members: [] });
    runs[runs.length - 1].members.push(w);
    prevRow = row;
  }

  const out = [];
  for (const run of runs) {
    const fill = run.members.length >= 2 && run.members.every((m) => m.isSingleNumber);
    if (!fill) {
      const spans = run.members.map((m) => responsiveSpan(m.authoredSpan, cols));
      out.push({
        row: run.row,
        fill,
        items: run.members.map((m, i) => ({ id: m.id, span: spans[i] })),
      });
      continue;
    }
    // MER-580: a filled KPI row wraps rather than squeezing. Seven cards over
    // 12 tracks gave base = floor(12/7) = 1, so the leading cards landed on a
    // single ~110px track while KPITile still painted its 180px minimum -- the
    // tiles overlapped their neighbours and their captions were clipped
    // mid-word. Cap the row at floor(cols / MIN_CARD_SPAN) and split the
    // overflow into BALANCED sub-rows, so 7 reads 4 + 3 (both full width)
    // rather than 6 + 1 (a full row then an orphan).
    for (const chunk of balancedChunks(run.members, maxCardsPerRow(cols))) {
      const spans = distributeRowSpans(chunk.length, cols);
      out.push({
        row: run.row,
        fill: true,
        items: chunk.map((m, i) => ({ id: m.id, span: spans[i] })),
      });
    }
  }
  return out;
}

/**
 * MER-580: the narrowest a single-number card may get, in 12-grid tracks.
 * Two tracks is about 185px at a 1440px viewport with the sidebar, which is
 * the width the tile's own type scale needs before the title wraps to three
 * lines and the caption starts clipping.
 */
export const MIN_CARD_SPAN = 2;

/** How many single-number cards fit on one row at `cols` tracks. */
export function maxCardsPerRow(cols) {
  const c = Number.isFinite(cols) ? Math.max(1, Math.floor(cols)) : 1;
  return Math.max(1, Math.floor(c / MIN_CARD_SPAN));
}

/**
 * Split `items` into as few chunks as possible, none larger than `max`, with
 * the sizes as equal as they can be. 7 with max 6 gives [4, 3], not [6, 1]:
 * two full-width rows read as a deliberate block, a full row plus an orphan
 * reads as a bug. Trailing chunks take the remainder so the first row is never
 * the short one.
 */
export function balancedChunks(items, max) {
  const list = Array.isArray(items) ? items : [];
  const cap = Number.isFinite(max) ? Math.max(1, Math.floor(max)) : 1;
  if (list.length <= cap) return list.length ? [list] : [];
  const rows = Math.ceil(list.length / cap);
  const base = Math.floor(list.length / rows);
  const remainder = list.length - base * rows;
  const out = [];
  let i = 0;
  for (let r = 0; r < rows; r += 1) {
    const size = base + (r < remainder ? 1 : 0);
    out.push(list.slice(i, i + size));
    i += size;
  }
  return out;
}
